import { cardLabel, newDecks, type Card } from '../cards';
import { luckOf, secondChanceFrame } from './luck';
import { THREE_CARD_NAMES, ThreeCardRank, compareScores, scoreThree } from '../poker';
import type { CasinoGame, TableOption } from './types';

/**
 * Three Card Poker, ante/play only. Put up the ante, see your three cards,
 * then Play (a second bet equal to the ante) or Fold (lose the ante).
 * The dealer needs queen-high to qualify: if not, the ante pays 1:1 and the
 * play bet pushes. Otherwise higher hand wins both bets at 1:1. Played
 * hands also collect the ante bonus (straight 1, trips 4, straight flush 5)
 * whatever the dealer has. Folding with anything under Q-6-4 is the
 * standard strategy; the edge is ~3.4% of the ante.
 */
export const ANTE_BONUS: Partial<Record<ThreeCardRank, number>> = {
  [ThreeCardRank.Straight]: 1,
  [ThreeCardRank.ThreeOfAKind]: 4,
  [ThreeCardRank.StraightFlush]: 5,
};

/** House Special: straights pay a 2x ante bonus (edge ~3.4% to ~0.1%). */
/** The ★'s luck: how often a dealer hand that beats you is re-dealt, by
 *  luck level. Calibrated by sim to 103% / 110%; pinned in luck.test.ts. */
export const SECOND_CHANCE = [0, 0.2, 0.48];

export function anteBonus(rank: ThreeCardRank): number {
  return ANTE_BONUS[rank] ?? 0;
}

export function dealerQualifies(score: number[]): boolean {
  return score[0]! > ThreeCardRank.HighCard || score[1]! >= 12;
}

/** Mini royal: A-K-Q of one suit. */
export function isMiniRoyal(cards: Card[]): boolean {
  const score = scoreThree(cards);
  return score[0] === ThreeCardRank.StraightFlush && score[1] === 14;
}

export interface ThreeCardState {
  phase: 'idle' | 'decide' | 'done';
  player: Card[];
  dealer: Card[];
  ante: number;
  played: boolean;
  returned: number;
  lines: string[];
}

/** Settles a played or folded hand. Total returned includes stakes. */
export function settleThreeCard(player: Card[], dealer: Card[], ante: number, played: boolean): { returned: number; lines: string[] } {
  if (!played) return { returned: 0, lines: [`Folded. -$${ante}`] };
  const mine = scoreThree(player);
  const theirs = scoreThree(dealer);
  const bonus = ante * anteBonus(mine[0] as ThreeCardRank);
  const lines: string[] = [];
  let returned = 0;
  if (!dealerQualifies(theirs)) {
    returned = ante * 2 + ante; // ante wins, play pushes
    lines.push('Dealer does not qualify.', `Ante +$${ante}, play pushes`);
  } else {
    const cmp = compareScores(mine, theirs);
    if (cmp > 0) {
      returned = ante * 4;
      lines.push(`You win +$${ante * 2}`);
    } else if (cmp === 0) {
      returned = ante * 2;
      lines.push('Push');
    } else lines.push(`Dealer wins -$${ante * 2}`);
  }
  if (bonus) lines.push(`Ante bonus +$${bonus}`);
  return { returned: returned + bonus, lines };
}

const HIGH_NAMES: Record<number, string> = { 14: 'A', 13: 'K', 12: 'Q', 11: 'J' };

/** The dealer's three cards turning over one at a time. */
function flipFrames(player: Card[], dealer: Card[]): string[] {
  return [1, 2, 3].map((n) => [`You: ${player.map(cardLabel).join(' ')}`, `Dealer: ${[...dealer.slice(0, n).map(cardLabel), ...Array(3 - n).fill('??')].join(' ')}`].join('\n'));
}

function describeHand(cards: Card[]): string {
  const score = scoreThree(cards);
  // "Q-high" rather than "High card": the dealer's line has to fit one row.
  const name = score[0] === ThreeCardRank.HighCard ? `${HIGH_NAMES[score[1]!] ?? score[1]}-high` : THREE_CARD_NAMES[score[0] as ThreeCardRank];
  return `${cards.map(cardLabel).join(' ')} ${name}`;
}

/** The standard "play Q-6-4 or better" rule, shown as a hint. */
export function shouldPlay(cards: Card[]): boolean {
  const score = scoreThree(cards);
  if (score[0]! > ThreeCardRank.HighCard) return true;
  return compareScores(score.slice(1), [12, 6, 4]) >= 0;
}

export const threeCardPoker: CasinoGame<ThreeCardState> = {
  id: 'threecard',
  usesLuck: true,
  name: 'Three Card Poker',
  blurb: 'Ante, see three cards, then play or fold vs the dealer.',
  baseMinBet: 5,
  baseMaxBet: 100,
  specialPerk: 'Luck: a dealer hand that beats you may be re-dealt.',
  jackpotRule: 'Play a mini royal (A-K-Q suited).',
  lockedHint: '(play or fold)',
  create: () => ({ phase: 'idle', player: [], dealer: [], ante: 0, played: false, returned: 0, lines: [] }),
  locked: (state) => state.phase === 'decide',
  options(state, env): TableOption[] {
    if (state.phase !== 'decide') return [{ id: 'deal', label: state.phase === 'done' ? 'Deal again' : 'Ante & deal' }];
    const opts: TableOption[] = [];
    if (env.bankroll >= state.ante) opts.push({ id: 'play', label: `Play (+$${state.ante})` });
    opts.push({ id: 'fold', label: 'Fold' });
    return opts;
  },
  describe(state, env) {
    if (state.phase === 'idle') return 'Your bet is the ante. Playing costs the same again. Dealer needs Q-high.';
    if (state.phase === 'decide') {
      const hint = shouldPlay(state.player) ? 'Worth playing.' : 'Below Q-6-4: most fold.';
      const broke = env.bankroll < state.ante ? '\nNot enough to Play.' : '';
      return `You: ${describeHand(state.player)}\nDealer: ?? ?? ??\n\n${hint}${broke}`;
    }
    const dealer = state.played ? `Dealer: ${describeHand(state.dealer)}` : 'Dealer: (not shown)';
    return [`You: ${describeHand(state.player)}`, dealer, '', ...state.lines].join('\n');
  },
  act(state, optionId, env) {
    if (state.phase !== 'decide') {
      if (optionId !== 'deal') return { state, bankroll: env.bankroll };
      const deck = env.rng.shuffle(newDecks(1));
      return {
        state: { phase: 'decide', player: deck.slice(0, 3), dealer: deck.slice(3, 6), ante: env.bet, played: false, returned: 0, lines: [] },
        bankroll: env.bankroll - env.bet,
      };
    }
    if (optionId !== 'play' && optionId !== 'fold') return { state, bankroll: env.bankroll };
    const played = optionId === 'play' && env.bankroll >= state.ante;
    if (optionId === 'play' && !played) return { state, bankroll: env.bankroll };
    const bankroll = env.bankroll - (played ? state.ante : 0);
    // The ★'s luck: if you play and the dealer beats you, sometimes the
    // dealer's three cards are dealt again.
    let dealer = state.dealer;
    let lucky = false;
    const dealerWins = (d: Card[]) => played && settleThreeCard(state.player, d, 1, true).returned < 2;
    if (dealerWins(dealer) && env.rng.next() < (SECOND_CHANCE[luckOf(env)] ?? 0)) {
      dealer = env.rng.shuffle(newDecks(1).filter((c) => !state.player.includes(c))).slice(0, 3);
      lucky = !dealerWins(dealer);
    }
    const settled = settleThreeCard(state.player, dealer, state.ante, played);
    // Played: the dealer's cards turn over; a ★ second chance shows the
    // beating hand first, then deals the dealer again.
    const frames = !played
      ? []
      : lucky
        ? [...flipFrames(state.player, state.dealer), secondChanceFrame(flipFrames(state.player, state.dealer).at(-1)!), ...flipFrames(state.player, dealer)]
        : flipFrames(state.player, dealer);
    const lines = lucky ? [`★ ${settled.lines[0]}`, ...settled.lines.slice(1)] : settled.lines;
    const returned = settled.returned;
    const wagered = state.ante * (played ? 2 : 1);
    return {
      state: { ...state, phase: 'done', played, dealer, returned, lines },
      bankroll: bankroll + returned,
      settled: { wagered, returned },
      jackpot: played && isMiniRoyal(state.player),
      frames,
    };
  },
};

